/**
 * The ordering configuration surface and the kitchen's daily controls
 * (FRS-55, ORDR-119).
 *
 * Committed as a placeholder by HQ before Wave 2C spawned (#20) and filled by
 * the w2c-config lane. `ConfigSurface`, `DailyControls`, `ConfigScope` and both
 * props interfaces are FROZEN: w2c-shell mounts them in parallel.
 *
 * The shell mounts `ConfigSurface` in the `/admin` panel slot and
 * `DailyControls` in the kitchen panel slot.
 *
 * RULING B9: everything here reads and writes ORDERING's own configuration
 * plane, `nodes/<uuid>/config/<DOMAIN>/`, never paio-config.
 */

import { createElement, type ReactElement } from "react";
import type { ApiClient } from "../../api/client.js";
import { ConfigSurfaceView } from "./ConfigSurface.js";
import { DailyControlsView } from "./DailyControls.js";

export { CONFIG_DOMAINS, READ_BY_ORDERING } from "./domains.js";
export { CADENCE_MODULES, OTHER_SECTION } from "./cadence.js";
export { WRITE_CAPABILITY } from "./capability.js";

/** The node being acted for, as the ordering context describes it. */
export interface ConfigScope {
  /** The acting node's id (`node_id` in `GET /ordering/context`). */
  readonly nodeId: string;
  /** Its label, for headings. */
  readonly nodeName: string;
  /** The Site that node trades at, or `null` when it has none. */
  readonly siteId: string | null;
  /**
   * The acting node's capabilities, verbatim from the ordering context. A
   * RENDERING input only (ruling B10); the server decides every write.
   */
  readonly capabilities: readonly string[];
}

export interface ConfigSurfaceProps {
  /** The app's one HTTP seam. */
  readonly client: ApiClient;
  readonly scope: ConfigScope;
}

export interface DailyControlsProps {
  /** The app's one HTTP seam. */
  readonly client: ApiClient;
  readonly scope: ConfigScope;
}

export function ConfigSurface(props: ConfigSurfaceProps): ReactElement | null {
  return createElement(ConfigSurfaceView, props);
}

export function DailyControls(props: DailyControlsProps): ReactElement | null {
  return createElement(DailyControlsView, props);
}
